/**
 * Admin → Failures: every import that failed or came back degraded, with the
 * evidence needed to reproduce it (PRP-IMPORT-FAIL-1 Slice 3).
 *
 * Before this screen, a failed upload was invisible: the user got nothing back
 * and the founder found out days later by reading the Users table and noticing
 * somebody had zero estimates. This is where that becomes a queue you can work.
 *
 * Two shapes carry meaning and are worth keeping:
 *
 *   * A row without a PDF is not a broken row. Every failure before 2026-08-28
 *     had its upload deleted on the way out (the behaviour B-2861 stopped), so
 *     `has_pdf: false` means *ask the user for the file* — which is exactly
 *     what the `needs_file` status says. The View PDF button is absent rather
 *     than dead.
 *   * The full `error_text` is behind an expand, because the server only sends
 *     200 characters to the list. Opening a row is a second request, on purpose.
 *
 * Promote (Slice 4) stages the PDF as a redacted fixture candidate. It reports
 * back what it replaced and, more importantly, what it could NOT classify — a
 * promotion is not finished until a human has read `redaction_report.md`.
 */
import React, { useEffect, useMemo, useState } from 'react';

import { BackendAPI } from '../../lib/api';

type Row = Awaited<ReturnType<typeof BackendAPI.adminListFailures>>['items'][number];

const STATUSES = ['new', 'triaged', 'needs_file', 'promoted', 'fixed', 'ignored'] as const;

/** What each status means in the founder's words, not the schema's. */
const STATUS_LABEL: Record<string, string> = {
  new: 'New',
  triaged: 'Triaged',
  needs_file: 'Needs file from user',
  promoted: 'Promoted to fixture',
  fixed: 'Fixed',
  ignored: 'Ignored',
};

const PAGE_SIZE = 50;

const fmtTime = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString() : '—';

const SeverityBadge: React.FC<{ severity: string }> = ({ severity }) => (
  <span
    className={
      'rounded-full px-2 py-0.5 text-xs font-medium ' +
      (severity === 'failed'
        ? 'bg-red-500/15 text-red-600 dark:text-red-400'
        : 'bg-amber-500/15 text-amber-600 dark:text-amber-400')
    }
  >
    {severity === 'failed' ? 'Failed' : 'Degraded'}
  </span>
);

/** Admin tab: the failed-import queue. */
interface FailuresTabProps {
  /** Failure to open on arrival (the alert email's deep link). */
  initialId?: string | null;
  initialSeverity?: string;
}

const FailuresTab: React.FC<FailuresTabProps> = ({ initialId = null, initialSeverity = '' }) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [status, setStatus] = useState('');
  const [severity, setSeverity] = useState(initialSeverity);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [detail, setDetail] = useState<Record<string, any> | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [promoting, setPromoting] = useState<string | null>(null);

  const load = (nextOffset = offset) => {
    setLoading(true);
    setError(null);
    BackendAPI.adminListFailures({
      ...(status ? { status } : {}),
      ...(severity ? { severity } : {}),
      limit: PAGE_SIZE,
      offset: nextOffset,
    })
      .then((res) => setRows(res.items))
      .catch((e) => setError(e?.message || 'Failed to load the failure queue'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(0);
    setOffset(0);
    // Filters are the query; re-run whenever they change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, severity]);

  useEffect(() => {
    if (initialId) expand({ id: initialId } as Row);
    // Once, on arrival from a deep link.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const expand = async (row: Row) => {
    if (expanded === row.id) {
      setExpanded(null);
      setDetail(null);
      return;
    }
    setExpanded(row.id);
    setDetail(null);
    try {
      setDetail(await BackendAPI.adminFailure(row.id));
    } catch (e: any) {
      setError(e?.message || 'Could not load the full error');
    }
  };

  const openPdf = async (row: Row) => {
    setError(null);
    try {
      const { url } = await BackendAPI.adminFailurePdf(row.id);
      window.open(url, '_blank', 'noopener');
    } catch (e: any) {
      setError(e?.message || 'The PDF could not be opened');
    }
  };

  const changeStatus = async (row: Row, next: string) => {
    setError(null);
    try {
      await BackendAPI.adminSetFailureStatus(row.id, next);
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, status: next } : r)));
    } catch (e: any) {
      setError(e?.message || 'Could not change the status');
    }
  };

  const promote = async (row: Row) => {
    setError(null);
    setNote(null);
    setPromoting(row.id);
    try {
      const res = await BackendAPI.adminPromoteFailure(row.id);
      if (res.redacted) {
        setRows((prev) =>
          prev.map((r) => (r.id === row.id ? { ...r, status: 'promoted' } : r)),
        );
        setNote(
          `Staged ${res.path} — ${res.replacements} replacement(s). ` +
            (res.unclassified.length
              ? `${res.unclassified.length} header line(s) could not be classified: ${res.unclassified.join('; ')}. `
              : '') +
            'Read redaction_report.md before committing anything.',
        );
      } else {
        // No text layer. Saying "promoted" here would claim a fixture exists.
        setNote(
          `${res.path} has no text layer, so nothing could be redacted automatically. ` +
            'A REDACTION_NEEDED.md was written; this one has to be redacted by hand.',
        );
      }
    } catch (e: any) {
      setError(e?.message || 'Could not promote this failure');
    } finally {
      setPromoting(null);
    }
  };

  /** The command that reproduces this failure locally, ready to paste. */
  const reproCommand = (row: Row) =>
    `# failed_imports ${row.id} — ${row.filename || 'unknown file'}\n` +
    `python -m app.cli.import --pdf <local path> --layout auto`;

  const copyRepro = async (row: Row) => {
    try {
      await navigator.clipboard.writeText(reproCommand(row));
      setNote('Reproduction command copied.');
    } catch {
      setNote('Could not reach the clipboard — select the command and copy it.');
    }
  };

  const failedCount = useMemo(
    () => rows.filter((r) => r.severity === 'failed').length,
    [rows],
  );

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
        Every import that failed outright or came back degraded (no line items, or
        more than a quarter unmatched). Rows with no PDF are failures from before
        we started keeping the file &mdash; those need the user to send it again.
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <select
          aria-label="Filter by status"
          className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s]}
            </option>
          ))}
        </select>
        <select
          aria-label="Filter by severity"
          className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          value={severity}
          onChange={(e) => setSeverity(e.target.value)}
        >
          <option value="">Failed and degraded</option>
          <option value="failed">Failed only</option>
          <option value="degraded">Degraded only</option>
        </select>
        <button
          type="button"
          className="rounded-md border border-border px-3 py-1.5 text-sm"
          onClick={() => load()}
          disabled={loading}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
        <span className="text-sm text-muted-foreground">
          {rows.length} shown, {failedCount} failed
        </span>
      </div>

      {error && <p className="text-sm font-medium text-red-500">{error}</p>}
      {note && <p className="text-sm text-muted-foreground">{note}</p>}

      {!loading && !rows.length && (
        <p className="text-sm text-muted-foreground">
          No failures match this filter. That&rsquo;s the answer you want.
        </p>
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th className="p-2">When</th>
                <th className="p-2">Who</th>
                <th className="p-2">File</th>
                <th className="p-2">Stage</th>
                <th className="p-2">Severity</th>
                <th className="p-2">Error</th>
                <th className="p-2">Seen</th>
                <th className="p-2">Status</th>
                <th className="p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <React.Fragment key={r.id}>
                  <tr
                    className="cursor-pointer border-t border-border hover:bg-muted/30"
                    onClick={() => expand(r)}
                  >
                    <td className="p-2 whitespace-nowrap">{fmtTime(r.created_at)}</td>
                    <td className="p-2 break-all">{r.email || r.user_id}</td>
                    <td className="p-2 break-all">{r.filename || '—'}</td>
                    <td className="p-2">{r.stage}</td>
                    <td className="p-2">
                      <SeverityBadge severity={r.severity} />
                    </td>
                    <td className="p-2 font-mono text-xs">{r.error_class || '—'}</td>
                    <td className="p-2">{r.occurrences}×</td>
                    <td className="p-2" onClick={(e) => e.stopPropagation()}>
                      <select
                        aria-label={`Status for ${r.filename || r.id}`}
                        className="rounded-md border border-border bg-background px-1.5 py-1 text-xs"
                        value={r.status}
                        onChange={(e) => changeStatus(r, e.target.value)}
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {STATUS_LABEL[s]}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="p-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {r.has_pdf ? (
                        <span className="flex gap-1">
                          <button
                            type="button"
                            className="rounded-md border border-border px-2 py-1 text-xs"
                            onClick={() => openPdf(r)}
                          >
                            View PDF
                          </button>
                          <button
                            type="button"
                            className="rounded-md border border-border px-2 py-1 text-xs disabled:opacity-50"
                            onClick={() => promote(r)}
                            disabled={promoting === r.id}
                          >
                            {promoting === r.id ? 'Promoting…' : 'Promote'}
                          </button>
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">No file kept</span>
                      )}
                    </td>
                  </tr>

                  {expanded === r.id && (
                    <tr className="border-t border-border bg-muted/20">
                      <td colSpan={9} className="p-3">
                        <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                          <span>Pages: {r.page_count ?? '—'}</span>
                          <span>Layout guess: {r.layout_guess || '—'}</span>
                          <span>Line items: {r.line_items ?? '—'}</span>
                          <span>Unmatched: {r.unmatched ?? '—'}</span>
                          <span>Backlog: {r.backlog_ref || '—'}</span>
                        </div>
                        <pre className="mt-2 max-h-64 overflow-auto rounded-md bg-background p-2 font-mono text-xs whitespace-pre-wrap">
                          {detail
                            ? String(detail.error_text || '(no error text recorded)')
                            : 'Loading the full error…'}
                        </pre>
                        <button
                          type="button"
                          className="mt-2 rounded-md border border-border px-2 py-1 text-xs"
                          onClick={() => copyRepro(r)}
                        >
                          Copy reproduction command
                        </button>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          className="rounded-md border border-border px-3 py-1.5 text-sm disabled:opacity-50"
          onClick={() => {
            const next = Math.max(0, offset - PAGE_SIZE);
            setOffset(next);
            load(next);
          }}
          disabled={loading || offset === 0}
        >
          Previous
        </button>
        <button
          type="button"
          className="rounded-md border border-border px-3 py-1.5 text-sm disabled:opacity-50"
          onClick={() => {
            const next = offset + PAGE_SIZE;
            setOffset(next);
            load(next);
          }}
          disabled={loading || rows.length < PAGE_SIZE}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default FailuresTab;
